import React, { ReactNode, useState } from 'react'
import { cn } from './cn'

export interface AvatarProps {
  src?: string
  alt?: string
  /** Shown when there is no image (or it fails to load): an emoji or a node. */
  emoji?: ReactNode
  /** Initials fallback; the first letter is used. */
  fallback?: string
  size?: 20 | 24 | 32 | 40 | 48 | 56 | 64 | number
  /** The heritage sweep ring around the avatar. */
  ring?: boolean
  square?: boolean
  className?: string
}

export const Avatar = ({
  src,
  alt = '',
  emoji,
  fallback,
  size = 40,
  ring,
  square,
  className,
}: AvatarProps) => {
  // Track which src failed, so a new src is tried again instead of staying on the fallback.
  const [failedSrc, setFailedSrc] = useState<string>()
  const showImage = src && failedSrc !== src
  const content = showImage ? (
    <img
      src={src}
      alt={alt}
      draggable={false}
      onError={() => setFailedSrc(src)}
      className="block size-full object-cover"
    />
  ) : emoji ? (
    <span style={{ fontSize: Math.round(size * 0.5) }} className="leading-none">
      {emoji}
    </span>
  ) : (
    <span
      style={{ fontSize: Math.round(size * 0.4) }}
      className="font-semibold leading-none text-fg-2"
    >
      {fallback?.trim()?.[0]?.toUpperCase()}
    </span>
  )

  const inner = (
    <span
      className={cn(
        'grid shrink-0 place-items-center overflow-hidden bg-surface-2',
        square ? 'rounded-sm' : 'rounded-full',
        !ring && className
      )}
      style={{ width: size, height: size }}
    >
      {content}
    </span>
  )

  if (!ring) return inner
  return (
    <span
      className={cn(
        'grid shrink-0 place-items-center rounded-full bg-[image:var(--plug-grad-mark)] p-[2px]',
        className
      )}
      style={{ width: size + 4, height: size + 4 }}
    >
      <span className="grid size-full place-items-center rounded-full bg-bg">{inner}</span>
    </span>
  )
}
